<script setup lang="ts">
   import { onMounted, ref, watch } from 'vue';
   import type { TournamentEntryFieldSignature } from '~/types/types';

   const props = defineProps<{
      field: TournamentEntryFieldSignature;
      modelValue: string | undefined;
      error?: string;
      disabled?: boolean;
   }>();

   const emit = defineEmits<{
      'update:modelValue': [value: string];
   }>();

   const canvasRef = ref<HTMLCanvasElement | null>(null);
   const isDrawing = ref(false);
   const hasStrokes = ref(false);
   let ctx: CanvasRenderingContext2D | null = null;
   let lastX = 0;
   let lastY = 0;

   const CANVAS_WIDTH = 600;
   const CANVAS_HEIGHT = 200;

   onMounted(() => {
      const canvas = canvasRef.value;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = CANVAS_WIDTH * dpr;
      canvas.height = CANVAS_HEIGHT * dpr;
      canvas.style.width = `${CANVAS_WIDTH}px`;
      canvas.style.height = `${CANVAS_HEIGHT}px`;

      ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
   });

   const getPoint = (event: PointerEvent | MouseEvent | TouchEvent) => {
      const canvas = canvasRef.value;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;

      if ('touches' in event && event.touches.length > 0) {
         const touch = event.touches[0];
         return {
            x: (touch.clientX - rect.left) * scaleX,
            y: (touch.clientY - rect.top) * scaleY,
         };
      }

      const ptr = event as PointerEvent | MouseEvent;
      return {
         x: (ptr.clientX - rect.left) * scaleX,
         y: (ptr.clientY - rect.top) * scaleY,
      };
   };

   const startStroke = (event: PointerEvent | MouseEvent | TouchEvent) => {
      if (props.disabled) return;
      event.preventDefault();
      isDrawing.value = true;
      const { x, y } = getPoint(event);
      lastX = x;
      lastY = y;
   };

   const drawStroke = (event: PointerEvent | MouseEvent | TouchEvent) => {
      if (!isDrawing.value || !ctx) return;
      event.preventDefault();
      const { x, y } = getPoint(event);

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();

      lastX = x;
      lastY = y;
      hasStrokes.value = true;
   };

   const endStroke = () => {
      if (!isDrawing.value) return;
      isDrawing.value = false;
      exportToModel();
   };

   const exportToModel = () => {
      const canvas = canvasRef.value;
      if (!canvas || !hasStrokes.value) {
         emit('update:modelValue', '');
         return;
      }
      const dataUrl = canvas.toDataURL('image/png');
      emit('update:modelValue', dataUrl);
   };

   const clear = () => {
      if (props.disabled) return;
      const canvas = canvasRef.value;
      if (!canvas || !ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      hasStrokes.value = false;
      emit('update:modelValue', '');
   };

   // When the model is reset externally (e.g., after submit reset), clear the canvas
   watch(
      () => props.modelValue,
      (next) => {
         if (!next && hasStrokes.value) {
            clear();
         }
      }
   );
</script>

<template>
   <div class="mb-4 flex flex-col gap-2">
      <label class="text-sm font-semibold text-gray-800">
         {{ field.label }}
         <span v-if="field.required" class="ml-0.5 text-red-700" aria-hidden="true">*</span>
      </label>

      <div
         class="relative max-w-[600px] overflow-hidden rounded-md border bg-white"
         :class="[
            error ? 'border-red-700' : 'border-gray-300',
            disabled ? 'bg-gray-100 opacity-70' : '',
         ]">
         <canvas
            ref="canvasRef"
            class="block h-[200px] w-full max-w-[600px] touch-none"
            :class="disabled ? 'cursor-not-allowed' : 'cursor-crosshair'"
            @pointerdown="startStroke"
            @pointermove="drawStroke"
            @pointerup="endStroke"
            @pointerleave="endStroke"
            @pointercancel="endStroke"
            @touchstart="startStroke"
            @touchmove="drawStroke"
            @touchend="endStroke"
            @touchcancel="endStroke" />
         <p
            v-if="!hasStrokes"
            class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none italic text-gray-400">
            Sign here
         </p>
      </div>

      <div class="flex max-w-[600px] justify-end">
         <button
            type="button"
            class="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="disabled || !hasStrokes"
            @click="clear">
            Clear
         </button>
      </div>

      <p v-if="error" class="text-xs text-red-700">{{ error }}</p>
   </div>
</template>
